import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';

const HOAS_COUNT = 3;

const CLIENT_HOA_FIELDS = [
  {
    key: 'name',
    label: 'HOA Name',
    type: 'text',
    required: true,
    placeholder: 'Association name',
    errorKeys: index => [`name-${index}`, `HOA${index}Name`],
    errorMessage: 'This is a required field',
  },
  {
    key: 'managementCompany',
    label: 'Management Company',
    type: 'text',
    placeholder: 'Optional',
  },
  {
    key: 'phoneNumber',
    label: 'Phone',
    type: 'tel',
  },
  {
    key: 'email',
    label: 'Email',
    type: 'email',
    errorKeys: index => [`email-${index}`, `HOA${index}Email`],
    errorMessage: 'Enter a valid email',
  },
];

const blankClientHoa = () => ({
  name: '',
  managementCompany: '',
  phoneNumber: '',
  email: '',
});

const normalizeClientHoa = (h = {}) => ({
  name: String(h.name || '').trim(),
  managementCompany: String(h.managementCompany || '').trim(),
  phoneNumber: String(h.phoneNumber || '').trim(),
  email: String(h.email || '').trim(),
});

/**
 * Simple text-only HOA entry for org/client new-order placement.
 * Submitted as requestHoaInfo for processor reference — not linked to the HOA directory.
 */
const ClientHoaTextForm = ({ value = [], onChange, errors = {}, twoColumnLayout = false }) => {
  const count = Math.min(
    HOAS_COUNT,
    Math.max(1, (Array.isArray(value) && value.length) || 1)
  );
  const slots = Array.from({ length: count }, (_, i) =>
    normalizeClientHoa((Array.isArray(value) && value[i]) || blankClientHoa())
  );

  const setCount = n => {
    let next = slots.map(normalizeClientHoa);
    if (n > next.length) {
      while (next.length < n) next.push(normalizeClientHoa(blankClientHoa()));
    } else {
      next = next.slice(0, n);
    }
    onChange(next);
  };

  const updateHoa = (index, fields) => {
    const next = slots.map(normalizeClientHoa);
    next[index] = normalizeClientHoa({ ...next[index], ...fields });
    onChange(next);
  };

  const hoaBlockColumnClass = twoColumnLayout ? 'col-md-6 mb-3' : 'mb-3';

  return (
    <div>
      <div className="mb-3">
        <div className="text-uppercase text-muted font-weight-bold xsmallFont mb-2 d-flex align-items-center">
          How many HOAs?
        </div>
        <div className="d-flex" style={{ gap: 8 }} role="group" aria-label="HOA count">
          {Array.from({ length: HOAS_COUNT }, (_, i) => i + 1).map(n => {
            const active = count === n;
            return (
              <button
                key={n}
                type="button"
                className={`midFont px-3 py-1 cursor-pointer border ${
                  active
                    ? 'font-weight-semibold border-info bg-info-light text-info'
                    : 'bg-white text-muted'
                }`}
                style={{ borderRadius: 10 }}
                onClick={() => setCount(n)}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>

      <div className={twoColumnLayout ? 'row' : undefined}>
        {slots.map((hoa, index) => (
          <div
            key={index}
            className={hoaBlockColumnClass}
          >
            <div className="p-3 border rounded h-100 client-hoa" style={{ borderColor: '#dee2e6' }}>
              {count > 1 && (
                <p className="font-weight-semibold midFont mb-2">HOA {index + 1}</p>
              )}
              <Row className="mx-n1">
                {CLIENT_HOA_FIELDS.map(field => {
                  const invalid = field.errorKeys
                    ? field.errorKeys(index).some(k => errors[k])
                    : false;
                  return (
                    <Col key={field.key} md={6} className="px-1 mb-2">
                      <Form.Group className="midFont mb-0">
                        <Form.Label>
                          {field.label}
                          {field.required && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type={field.type}
                          value={hoa[field.key]}
                          className={invalid ? 'input-invalid' : ''}
                          placeholder={field.placeholder}
                          onChange={e => updateHoa(index, { [field.key]: e.target.value })}
                        />
                        {invalid && field.errorMessage && (
                          <p className="xsmallFont text-rush mb-0 mt-1">{field.errorMessage}</p>
                        )}
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClientHoaTextForm;
